import { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';

export interface ImageViewerItem {
  id: string;
  url: string;
  caption?: string | null;
}

interface ImageViewerProps {
  images: ImageViewerItem[];
}

/** Các mức zoom cho phép trong Dialog xem toàn màn hình. */
const ZOOM_LEVELS = [1, 1.5, 2, 2.5, 3];

/**
 * Lưới ảnh chụp màn hình của một feature — bấm vào để phóng to trong Dialog,
 * có thể điều hướng ảnh trước/sau khi đang xem.
 */
export function ImageViewer({ images }: ImageViewerProps): JSX.Element {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [zoomIndex, setZoomIndex] = useState(0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragState = useRef<{ startX: number; startY: number; panX: number; panY: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  // Zoom riêng cho khung ảnh xem trước lớn (chưa mở fullscreen) — độc lập
  // với zoom của Dialog vì hai khung có thể không mở cùng lúc.
  const [previewZoomIndex, setPreviewZoomIndex] = useState(0);
  const [previewPan, setPreviewPan] = useState({ x: 0, y: 0 });
  const previewDragState = useRef<{ startX: number; startY: number; panX: number; panY: number } | null>(null);
  const [previewDragging, setPreviewDragging] = useState(false);

  if (images.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          py: 3,
          color: 'text.secondary',
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <ImageNotSupportedIcon fontSize="large" />
        <Typography variant="body2">Chưa có ảnh minh hoạ</Typography>
      </Box>
    );
  }

  const selected = images[selectedIndex] ?? images[0];
  const current = openIndex !== null ? images[openIndex] : null;
  const zoom = ZOOM_LEVELS[zoomIndex];
  const previewZoom = ZOOM_LEVELS[previewZoomIndex];

  const resetPreviewZoom = () => {
    setPreviewZoomIndex(0);
    setPreviewPan({ x: 0, y: 0 });
  };

  const selectThumbnail = (idx: number) => {
    setSelectedIndex(idx);
    resetPreviewZoom();
  };

  const previewZoomIn = () => setPreviewZoomIndex((i) => Math.min(i + 1, ZOOM_LEVELS.length - 1));

  const previewZoomOut = () => {
    setPreviewZoomIndex((i) => {
      const next = Math.max(i - 1, 0);
      if (next === 0) setPreviewPan({ x: 0, y: 0 });
      return next;
    });
  };

  const handlePreviewPointerDown = (e: React.PointerEvent<HTMLImageElement>) => {
    if (previewZoom <= 1) return;
    previewDragState.current = { startX: e.clientX, startY: e.clientY, panX: previewPan.x, panY: previewPan.y };
    setPreviewDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePreviewPointerMove = (e: React.PointerEvent<HTMLImageElement>) => {
    if (!previewDragState.current) return;
    const { startX, startY, panX, panY } = previewDragState.current;
    setPreviewPan({ x: panX + (e.clientX - startX), y: panY + (e.clientY - startY) });
  };

  const handlePreviewPointerUp = (e: React.PointerEvent<HTMLImageElement>) => {
    if (!previewDragState.current) return;
    previewDragState.current = null;
    setPreviewDragging(false);
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  const resetZoom = () => {
    setZoomIndex(0);
    setPan({ x: 0, y: 0 });
  };

  const openViewer = (idx: number) => {
    resetZoom();
    setOpenIndex(idx);
  };

  const closeViewer = () => {
    setOpenIndex(null);
    resetZoom();
  };

  const goto = (delta: number) => {
    resetZoom();
    setOpenIndex((idx) => {
      if (idx === null) return idx;
      const next = (idx + delta + images.length) % images.length;
      return next;
    });
  };

  const zoomIn = () => setZoomIndex((i) => Math.min(i + 1, ZOOM_LEVELS.length - 1));

  const zoomOut = () => {
    setZoomIndex((i) => {
      const next = Math.max(i - 1, 0);
      if (next === 0) setPan({ x: 0, y: 0 });
      return next;
    });
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLImageElement>) => {
    if (zoom <= 1) return;
    dragState.current = { startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y };
    setDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLImageElement>) => {
    if (!dragState.current) return;
    const { startX, startY, panX, panY } = dragState.current;
    setPan({ x: panX + (e.clientX - startX), y: panY + (e.clientY - startY) });
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLImageElement>) => {
    if (!dragState.current) return;
    dragState.current = null;
    setDragging(false);
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
          gap: 1.5,
          mb: 1.5,
        }}
      >
        {images.map((img, idx) => (
          <Box
            key={img.id}
            component="button"
            type="button"
            onClick={() => selectThumbnail(idx)}
            sx={{
              p: 0,
              border: '2px solid',
              borderColor: idx === selectedIndex ? 'primary.main' : 'divider',
              borderRadius: 1.5,
              overflow: 'hidden',
              cursor: 'pointer',
              bgcolor: 'background.paper',
              aspectRatio: '4 / 3',
              '&:hover': { borderColor: 'primary.main' },
            }}
          >
            <Box
              component="img"
              src={img.url}
              alt={img.caption ?? 'Ảnh minh hoạ'}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </Box>
        ))}
      </Box>

      {/* Vùng ảnh lớn hiển thị sẵn ngay dưới lưới thumbnail — người dùng đỡ
          phải bấm vào mới xem được ảnh phóng to. */}
      {selected && (
        <Box
          sx={{
            position: 'relative',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            overflow: 'hidden',
            bgcolor: 'black',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 240,
          }}
        >
          <Box
            component="img"
            src={selected.url}
            alt={selected.caption ?? 'Ảnh minh hoạ'}
            onPointerDown={handlePreviewPointerDown}
            onPointerMove={handlePreviewPointerMove}
            onPointerUp={handlePreviewPointerUp}
            onPointerLeave={handlePreviewPointerUp}
            sx={{
              width: '100%',
              maxHeight: 420,
              objectFit: 'contain',
              display: 'block',
              transform: `translate(${previewPan.x}px, ${previewPan.y}px) scale(${previewZoom})`,
              transition: previewDragging ? 'none' : 'transform 0.15s ease-out',
              cursor: previewZoom > 1 ? (previewDragging ? 'grabbing' : 'grab') : 'default',
              touchAction: 'none',
              userSelect: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <IconButton
              aria-label="Thu nhỏ"
              size="small"
              onClick={previewZoomOut}
              disabled={previewZoomIndex === 0}
              sx={{ color: 'white', bgcolor: 'rgba(0,0,0,0.4)' }}
            >
              <ZoomOutIcon fontSize="small" />
            </IconButton>
            <IconButton
              aria-label="Phóng to"
              size="small"
              onClick={previewZoomIn}
              disabled={previewZoomIndex === ZOOM_LEVELS.length - 1}
              sx={{ color: 'white', bgcolor: 'rgba(0,0,0,0.4)' }}
            >
              <ZoomInIcon fontSize="small" />
            </IconButton>
            {previewZoomIndex !== 0 && (
              <IconButton
                aria-label="Đặt lại zoom"
                size="small"
                onClick={resetPreviewZoom}
                sx={{ color: 'white', bgcolor: 'rgba(0,0,0,0.4)' }}
              >
                <RestartAltIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          <IconButton
            aria-label="Phóng to toàn màn hình"
            onClick={() => openViewer(selectedIndex)}
            sx={{ position: 'absolute', top: 8, right: 8, color: 'white', bgcolor: 'rgba(0,0,0,0.4)' }}
          >
            <FullscreenIcon />
          </IconButton>
          {selected.caption && (
            <Typography
              variant="body2"
              sx={{
                position: 'absolute',
                bottom: 8,
                left: '50%',
                transform: 'translateX(-50%)',
                color: 'white',
                bgcolor: 'rgba(0,0,0,0.5)',
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
              }}
            >
              {selected.caption}
            </Typography>
          )}
        </Box>
      )}

      <Dialog open={openIndex !== null} onClose={closeViewer} maxWidth="lg">
        <DialogContent
          sx={{
            position: 'relative',
            p: 0,
            bgcolor: 'black',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 320,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <IconButton
              aria-label="Thu nhỏ"
              onClick={zoomOut}
              disabled={zoomIndex === 0}
              sx={{ color: 'white' }}
            >
              <ZoomOutIcon />
            </IconButton>
            <IconButton
              aria-label="Phóng to"
              onClick={zoomIn}
              disabled={zoomIndex === ZOOM_LEVELS.length - 1}
              sx={{ color: 'white' }}
            >
              <ZoomInIcon />
            </IconButton>
            {zoomIndex !== 0 && (
              <IconButton aria-label="Đặt lại zoom" onClick={resetZoom} sx={{ color: 'white' }}>
                <RestartAltIcon />
              </IconButton>
            )}
            <IconButton aria-label="Đóng" onClick={closeViewer} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {images.length > 1 && (
            <IconButton
              aria-label="Ảnh trước"
              onClick={() => goto(-1)}
              sx={{ position: 'absolute', left: 8, color: 'white', zIndex: 1 }}
            >
              <NavigateBeforeIcon fontSize="large" />
            </IconButton>
          )}

          {current && (
            <Box
              component="img"
              src={current.url}
              alt={current.caption ?? 'Ảnh minh hoạ'}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
              sx={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transition: dragging ? 'none' : 'transform 0.15s ease-out',
                cursor: zoom > 1 ? (dragging ? 'grabbing' : 'grab') : 'default',
                touchAction: 'none',
                userSelect: 'none',
              }}
            />
          )}

          {images.length > 1 && (
            <IconButton
              aria-label="Ảnh sau"
              onClick={() => goto(1)}
              sx={{ position: 'absolute', right: 8, color: 'white', zIndex: 1 }}
            >
              <NavigateNextIcon fontSize="large" />
            </IconButton>
          )}

          {current?.caption && (
            <Typography
              variant="body2"
              sx={{
                position: 'absolute',
                bottom: 8,
                left: '50%',
                transform: 'translateX(-50%)',
                color: 'white',
                bgcolor: 'rgba(0,0,0,0.5)',
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
              }}
            >
              {current.caption}
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
